/**
 * Centralised runtime configuration.
 *
 * Two ways to talk to OpenRouteService:
 *   - Direct  (default for dev / Capacitor): frontend ships the ORS key,
 *            you restrict the key by HTTP referer in the ORS dashboard.
 *   - Proxy   (recommended for production on Cloudflare Pages): the
 *            frontend talks to /v2/... on its own origin and the Pages
 *            Function in functions/v2/[[path]].ts forwards to ORS with
 *            the secret ORS_API_KEY Pages secret.
 *
 * Auto-detect: if neither VITE_ORS_KEY nor VITE_ORS_PROXY_URL is set AND
 * the runtime is a browser, proxyUrl defaults to window.location.origin, so
 * the deployed PWA "just works" without manual env configuration. Set
 * VITE_ORS_PROXY_URL explicitly only if you front the site with a custom
 * domain other than the *.pages.dev default.
 */

const rawProxy = (import.meta.env.VITE_ORS_PROXY_URL ?? '').trim().replace(/\/+$/, '');
const rawKey = (import.meta.env.VITE_ORS_KEY ?? '').trim();

const runtimeProxy =
  !rawProxy && !rawKey && typeof window !== 'undefined'
    ? window.location.origin.replace(/\/+$/, '')
    : '';

/** The effective proxy URL — explicit env var wins, else auto-detected. */
const effectiveProxy = rawProxy || runtimeProxy;

export const config = {
  /**
   * Budapest centre. Used ONLY as the first-ever-boot fallback when no
   * GPS fix is available AND no saved location exists in localStorage.
   * Runtime centres are dynamic: navigator.geolocation wins (async),
   * then loadSavedLocation() (sync), then this constant. Geocode search
   * also clamps to a circle around this focus.
   */
  defaultCenter: { lat: 47.4979, lng: 19.0402 } as const,
  /** Slightly wider than street level so a brand-new user sees the city outline. */
  defaultZoom: 12,

  // Tile URL list lives inline in src/map.ts as `OSM_TILE_URLS`.
  // The previous `tileUrl` field used Leaflet's `{s}` substring which is
  // literally expanded by Leaflet but NOT by MapLibre (tile fetches
  // fail with HTTP 000). src/map.ts already hard-codes the three OSM
  // subdomain URLs explicitly so MapLibre can round-robin across them.

  tileAttribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',

  orsKey: rawKey,
  proxyUrl: effectiveProxy,

  /** Mode used by the network layer. */
  mode: effectiveProxy
    ? ('proxy' as const)
    : rawKey
      ? ('direct' as const)
      : ('invalid' as const),
} as const;

export function assertConfigured(): void {
  if (config.mode === 'invalid') {
    throw new Error(
      'Hiányzó API konfiguráció: töltsd ki a .env.local fájlt (VITE_ORS_KEY vagy VITE_ORS_PROXY_URL).',
    );
  }
}
